import { Card } from '@chakra-ui/react';
import React, { useState } from 'react';
import { useGooglePlaces, UseGooglePlacesConfig, GooglePlaceItem } from '../hooks';
import { GoogleOfficialIcon, SearchIcon } from './icons';
import Autocomplete, { AutocompleteOption } from './Autocomplete';
import { parseGqlError, TranslationFunction } from '../utils';

const styleBox = {
  borderRadius: 'lg',
  w: 'full',
  bg: 'bg.light',
  px: { base: 'md', md: 'xl' },
  pt: 'md',
};

interface BusinessSearchProps {
  t?: TranslationFunction;
  label?: string;
  placeholder?: string;
  helperText?: string;
  onSelect?: (data: any) => void;
  value?: AutocompleteOption | null;
  onChange?: (value: AutocompleteOption | null) => void;
  onError?: (error: string) => void;
  isRequired?: boolean;
  googlePlacesConfig: UseGooglePlacesConfig;
  [key: string]: any;
}

const BusinessSearch: React.FC<BusinessSearchProps> = ({
  t,
  label,
  placeholder,
  helperText,
  onSelect,
  value,
  onChange,
  onError,
  isRequired,
  googlePlacesConfig,
  ...style
}) => {
  const [error, setError] = useState<string>('');

  const { getGooglePlaceID, getPlaceDetails } = useGooglePlaces(googlePlacesConfig);

  const handleSearch = async (searchValue: string) => {
    try {
      const data = await getGooglePlaceID({
        input: searchValue,
      });

      setError('');
      onError?.('');

      return data
        ? data?.map((item: GooglePlaceItem) => ({
            label: item.description,
            value: item.place_id,
          }))
        : [];
    } catch (error) {
      const parseError = t ? parseGqlError(error as any, t) : { message: 'Not found' };
      const errorMessage = parseError.message;
      setError(errorMessage);
      onError?.(errorMessage);
      return [];
    }
  };

  const handleSelect = async (input: AutocompleteOption | null) => {
    onChange?.(input);

    if (input && input.value) {
      const data = await getPlaceDetails({
        placeId: input.value,
        fields: [
          'addressComponents',
          'generativeSummary',
          'nationalPhoneNumber',
          'formattedAddress',
          'displayName',
          'websiteUri',
          'editorialSummary',
          'types',
          'regularOpeningHours',
          'utcOffsetMinutes',
          'googleMapsUri',
        ],
      });
      onSelect?.(data);
    }
  };

  return (
    <Card
      {...styleBox}
      {...style}
    >
      <Autocomplete
        getOptions={handleSearch}
        isRequired={isRequired}
        isTruncated
        label={label || (t ? t('location.google-place-input-label') : 'Search for business')}
        leftElement={
          <SearchIcon
            color='primary.pure'
            boxSize={5}
          />
        }
        placeholder={
          placeholder ||
          (t ? t('location.google-place-input-placeholder') : 'Enter business name or address')
        }
        rightElement={<GoogleOfficialIcon boxSize={4} />}
        initialValue={value || undefined}
        onChange={handleSelect}
        pr='4rem'
        bgColor='white'
        name='businessSearch'
        invalid={!!error}
        errors={error ? { businessSearch: error } : {}}
        helperText={helperText}
      />
    </Card>
  );
};

export default BusinessSearch;
